import type {
    ProposalExtraItemType,
    ProposalProductItemType,
    ProductSetFullType,
    ProductType,
    ServiceType,
} from '@econtractor/types';
import * as apiProduct from 'api/apiProducts';
import * as apiProductSet from 'api/apiProductSets';
import * as apiService from 'api/apiServices';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing';
import { buildCatalogPricePatch } from './templatePriceUtils';

const uniquePositiveIds = (ids: Array<number | undefined | null>) =>
    Array.from(
        new Set(
            ids
                .map((id) => safeIntParse(id || 0) || 0)
                .filter((id) => id > 0),
        ),
    );

export const syncProductRowsPrices = async (
    products: ProposalProductItemType[],
    rate: number,
): Promise<ProposalProductItemType[]> => {
    if (!products.length) return products;

    const productIds = uniquePositiveIds(products.map((row) => row.productId));
    const setUnitIds = uniquePositiveIds(
        products
            .filter((row) => row.asSetUnit && row.sourceType === 'set')
            .map((row) => row.sourceId),
    );

    if (!productIds.length && !setUnitIds.length) return products;

    const productsMap: Record<number, ProductType> = {};
    if (productIds.length) {
        const productsRes = await apiProduct.getList({
            Ids: productIds,
            onPage: productIds.length,
        });
        if (!productsRes) {
            throw new Error('Failed to load product prices');
        }
        (productsRes.products || []).forEach((product) => {
            productsMap[Number(product.ID)] = product;
        });
    }

    const setsMap: Record<number, { totalPrice: number; totalPriceSale: number }> = {};
    if (setUnitIds.length) {
        const setResults = await Promise.all(
            setUnitIds.map((setId) => apiProductSet.getProductSet(setId)),
        );
        const loaded = setResults.filter(Boolean) as ProductSetFullType[];
        if (!loaded.length) {
            throw new Error('Failed to load set prices');
        }
        setUnitIds.forEach((setId, index) => {
            const setData = setResults[index];
            if (!setData) return;
            const totalPrice = safeFloatParse(setData.totalPrice || 0) || 0;
            const totalPriceSale = safeFloatParse(setData.totalPriceSale || totalPrice) || 0;
            setsMap[setId] = { totalPrice, totalPriceSale };
        });
    }

    return products.map((row) => {
        if (row.asSetUnit && row.sourceType === 'set') {
            const setId = safeIntParse(row.sourceId || 0) || 0;
            const setPrices = setsMap[setId];
            if (!setPrices) return row;
            return {
                ...row,
                ...buildCatalogPricePatch(setPrices.totalPrice, setPrices.totalPriceSale, rate),
            };
        }

        const productId = safeIntParse(row.productId || 0) || 0;
        if (productId <= 0) return row;
        const product = productsMap[productId];
        if (!product) return row;

        const cost = safeFloatParse(product.prodDefPrice || 0) || 0;
        const sale = safeFloatParse(product.prodDefPriceSale || cost) || 0;
        return {
            ...row,
            ...buildCatalogPricePatch(cost, sale, rate),
        };
    });
};

export const syncAdditionalRowsPrices = async (
    items: ProposalExtraItemType[],
    rate: number,
): Promise<ProposalExtraItemType[]> => {
    if (!items.length) return items;

    const serviceIds = uniquePositiveIds(items.map((row) => row.serviceId));
    if (!serviceIds.length) return items;

    const servicesRes = await apiService.getList({
        Ids: serviceIds,
        onPage: serviceIds.length,
    });
    if (!servicesRes) {
        throw new Error('Failed to load service prices');
    }

    const servicesMap: Record<number, ServiceType> = {};
    (servicesRes.services || []).forEach((service) => {
        servicesMap[Number(service.ID)] = service;
    });

    return items.map((row) => {
        const serviceId = safeIntParse(row.serviceId || 0) || 0;
        if (serviceId <= 0) return row;
        const service = servicesMap[serviceId];
        if (!service) return row;

        const cost = safeFloatParse(service.servDefPrice || 0) || 0;
        const sale = safeFloatParse(service.servDefPriceSale || cost) || 0;
        return {
            ...row,
            ...buildCatalogPricePatch(cost, sale, rate),
        };
    });
};

export const hasSyncableProductPrices = (products: ProposalProductItemType[]) =>
    products.some(
        (row) =>
            (safeIntParse(row.productId || 0) || 0) > 0 ||
            (Boolean(row.asSetUnit) &&
                row.sourceType === 'set' &&
                (safeIntParse(row.sourceId || 0) || 0) > 0),
    );

export const hasSyncableAdditionalPrices = (items: ProposalExtraItemType[]) =>
    items.some((row) => (safeIntParse(row.serviceId || 0) || 0) > 0);
